import { reactive, computed } from 'vue';
import z from 'zod';
import isNumeric from 'validator/lib/isNumeric';
import { policyOverrideService, policyService } from '@/services/api';
import {
  PolicyOverride,
  PolicyLineOverride,
  OriginalValues,
  type PolicyOverrideBatchPayload,
  type SublimitOpPayload,
  type SubretentionOpPayload,
} from '@/types/PolicyOverrides';
import { policyFieldLabels } from '@/components/v2/policy/policyFieldLabels';
import { compactMoney } from '@/utilities/Money';
import {
  subitemLabel,
  type PolicySublimit,
  type PolicySubretention,
  type SubitemKind,
  type SubitemTypeRef,
} from '@/types/PolicySublimit';

export type { SubitemKind };

type SubitemStatus = 'unchanged' | 'added' | 'updated' | 'deleted';

// One sublimit/subretention row as the panel renders it: a saved item, a staged addition, or a
// saved item with a staged edit or removal. `savedValue` is absent on a staged add; `originalValue`
// is absent on any user-created item, which is what distinguishes it from an AI/Epic-sourced one.
export interface SubitemRow {
  rowKey: string;
  kind: SubitemKind;
  itemId?: number;
  typeId: number;
  name: string;
  value: string;
  basis: string;
  savedValue?: number;
  savedBasis?: string;
  originalValue?: number;
  status: SubitemStatus;
  // Removed by a staged coverage type change: it can only come back by undoing that change.
  lockedByCoverageChange?: boolean;
}

interface StagedSubitem {
  kind: SubitemKind;
  op: 'add' | 'update' | 'delete';
  rowKey: string;
  itemId?: number;
  typeId?: number;
  typeName?: string;
  value?: string;
  basis?: string;
  cause?: 'coverageType';
}

export type RecordKey = 'policy' | `line:${string}`;

export interface RecordRef {
  key: RecordKey;
  type: 'policy' | 'line';
  uniqPolicy: string;
  uniqLine?: string;
  label: string;
  subLabel?: string;
}

const numericString = z
  .string()
  .trim()
  .refine((val) => val === '' || isNumeric(val), { message: 'Must be a number' })
  .default('');

export const formSchema = z.object({
  peFlag: z.string().trim().default(''),
  attachmentPointAmount: numericString,
  attachmentApplicable: z.boolean().default(true),
  aggregateLimitAmount: numericString,
  aggregateLimitApplicable: z.boolean().default(true),
  occurrenceLimitAmount: numericString,
  occurrenceLimitApplicable: z.boolean().default(true),
  quotaShareTotal: numericString,
  retentionAmount: numericString,
  retentionApplicable: z.boolean().default(true),
  leadSyndicateId: z.string().trim().default(''),
  sideAValue: z.string().trim().default(''),
  broker: z.string().trim().default(''),
  runoff: z.boolean().default(false),
  carrierId: numericString,
  premiumTotal: numericString,
  description: z.string().trim().default(''),
  policyNumber: z.string().trim().default(''),
  wholesaler: z.string().trim().default(''),
  agencyName: z.string().trim().default(''),
  coverageType: z.string().trim().default(''),
  // YYYY-MM-DD, as DatePickerInput emits it.
  effectiveDate: z.string().trim().default(''),
  expirationDate: z.string().trim().default(''),
  isQuotaShare: z.boolean().default(false),
});

export type FormSchemeType = z.infer<typeof formSchema>;

export type ValidFormFields = { [K in keyof FormSchemeType]: boolean };

// Client-readiness rule flags for the record being edited; the form panel
// marks the corresponding fields until the violation is resolved.
export interface PolicyRuleViolationFlags {
  noLimit?: boolean;
  invalidRetention?: boolean;
  invalidAttachment?: boolean;
}

const allFieldsValid = (): ValidFormFields => ({
  peFlag: true,
  attachmentPointAmount: true,
  attachmentApplicable: true,
  aggregateLimitAmount: true,
  aggregateLimitApplicable: true,
  occurrenceLimitAmount: true,
  occurrenceLimitApplicable: true,
  quotaShareTotal: true,
  retentionAmount: true,
  retentionApplicable: true,
  leadSyndicateId: true,
  sideAValue: true,
  broker: true,
  runoff: true,
  carrierId: true,
  premiumTotal: true,
  description: true,
  policyNumber: true,
  wholesaler: true,
  agencyName: true,
  coverageType: true,
  effectiveDate: true,
  expirationDate: true,
  isQuotaShare: true,
});

// The upload template rejects aggregate and occurrence limits on this coverage type.
const WORK_COMP_COVERAGE_TYPE = 'Work Comp';

export interface DiffEntry {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  // Optional: when a change is restricted (e.g. policy-source restriction in the AI
  // review flow) it is shown struck-through with a reason. Unused by the manual edit flow.
  restricted?: boolean;
  restrictionReason?: string;
}

// PolicyEditReviewPanel's input. AI extraction review also shows account changes, which have
// no edit record of their own, so the panel's ref type is wider than RecordRef.
export interface ReviewRecordRef extends Omit<RecordRef, 'key' | 'type'> {
  key: RecordKey | 'account';
  type: RecordRef['type'] | 'account';
}

export interface ReviewRecord {
  ref: ReviewRecordRef;
  diffs: DiffEntry[];
}

interface RecordState {
  ref: RecordRef;
  loaded: boolean;
  form: FormSchemeType;
  originalForm: FormSchemeType;
  validFields: ValidFormFields;
  // Why a field is invalid, where the reason is more than "required" or "invalid amount".
  validationMessages: Partial<Record<keyof FormSchemeType, string>>;
  loadedOverride: Partial<PolicyOverride>;
  sublimits: PolicySublimit[];
  subretentions: PolicySubretention[];
  sublimitTypes: SubitemTypeRef[];
  subretentionTypes: SubitemTypeRef[];
  subitemTypesLoaded: boolean;
  stagedSubitems: StagedSubitem[];
  // The staged subitems as they were before a coverage type change replaced them with removals;
  // set only while that change is staged, and restored when it is undone.
  coverageChangeSnapshot?: StagedSubitem[];
}

// Carrier/premium overrides are only writable for certain policy sources, and the carrier
// Select stores an rInsurerGroup ID while the override stores the group name — so these caps
// carry the id<->name lookup and the source's edit permissions. Configured via configureCaps().
interface PolicyCaps {
  carrierIdToName: Map<string, string>;
  carrierNameToId: Map<string, string>;
  allowCarrierUpdate: boolean;
  allowPremiumUpdate: boolean;
  allowExternalFieldUpdate: boolean;
}

const externalFields = [
  { form: 'description', override: 'descriptionOverride' },
  { form: 'policyNumber', override: 'policyNumberOverride' },
  { form: 'wholesaler', override: 'wholesalerOverride' },
  { form: 'agencyName', override: 'agencyNameOverride' },
  { form: 'coverageType', override: 'coverageTypeOverride' },
  { form: 'effectiveDate', override: 'effectiveDateOverride' },
  { form: 'expirationDate', override: 'expirationDateOverride' },
] as const;

const requiredOnceSetFields = ['quotaShareTotal', 'premiumTotal', 'leadSyndicateId', 'policyNumber'] as const;

interface FieldGroup<O> {
  form: (keyof FormSchemeType)[];
  override: (keyof O)[];
}

const lineFieldGroups: FieldGroup<PolicyLineOverride>[] = [
  { form: ['attachmentPointAmount', 'attachmentApplicable'], override: ['attachmentOverride', 'noAttachment'] },
  { form: ['aggregateLimitAmount', 'aggregateLimitApplicable'], override: ['aggregateLimitOverride', 'noLimit'] },
  { form: ['occurrenceLimitAmount', 'occurrenceLimitApplicable'], override: ['occurrenceLimitOverride', 'noOccurrenceLimit'] },
  { form: ['quotaShareTotal'], override: ['quotaShareTotalOverride'] },
  { form: ['retentionAmount', 'retentionApplicable'], override: ['retentionOverride', 'noRetention'] },
  { form: ['premiumTotal'], override: ['policyPremiumOverride'] },
];

const policyFieldGroups: FieldGroup<PolicyOverride>[] = [
  { form: ['peFlag'], override: ['peFlag'] },
  ...lineFieldGroups,
  { form: ['leadSyndicateId'], override: ['syndicateId'] },
  { form: ['sideAValue'], override: ['sideAOverride'] },
  { form: ['runoff'], override: ['runoffOverride'] },
  { form: ['carrierId'], override: ['carrierOverride'] },
  ...externalFields.map((field) => ({ form: [field.form], override: [field.override] })),
  { form: ['isQuotaShare'], override: ['quotaShareOverride'] },
];

// The override row is a full snapshot, so an untouched field resends the override it was loaded
// with. Resending the displayed original instead would pin it as an override, hiding later Epic or
// upload changes, and log a phantom change in the history. For the carrier it would also normalize
// the group to its primary issuing company.
function carryUntouched<O extends object>(
  fromForm: O,
  loaded: Partial<O>,
  form: FormSchemeType,
  original: FormSchemeType,
  groups: FieldGroup<O>[],
): O {
  const merged = { ...fromForm };
  for (const group of groups) {
    if (group.form.some((key) => form[key] !== original[key])) continue;
    for (const key of group.override) {
      merged[key] = (loaded[key] ?? undefined) as O[keyof O];
    }
  }
  return merged;
}

function hasApplicableLimit(form: FormSchemeType): boolean {
  const isSet = (amount: string) => amount.trim() !== '' && Number(amount) !== 0;
  return (form.aggregateLimitApplicable && isSet(form.aggregateLimitAmount)) || (form.occurrenceLimitApplicable && isSet(form.occurrenceLimitAmount));
}

// The API sends dates as ISO datetimes; the date picker works in YYYY-MM-DD.
const toDateInput = (value?: string | null): string => (value ? value.split('T')[0] : '');

// Same conversion as the quote dialogs: the picked day at local midnight, as an ISO datetime.
const toIsoDatetime = (date: string): string | undefined => (date ? new Date(`${date}T00:00:00`).toISOString() : undefined);

function toForm(originals: Partial<OriginalValues>, overrides: Partial<PolicyOverride>, carrierNameToId: Map<string, string>): FormSchemeType {
  const peFlag = overrides.peFlag || originals.peFlag || '';
  const hasRetention = !!(overrides.retentionOverride || originals.retention);
  const hasAttachment = !!(overrides.attachmentOverride || originals.attachment);

  return {
    peFlag,
    attachmentPointAmount: overrides.attachmentOverride?.toString() || originals.attachment?.toString() || '',
    attachmentApplicable: peFlag === 'Primary' && !hasAttachment ? false : overrides.noAttachment === undefined ? true : !overrides.noAttachment,
    aggregateLimitAmount: overrides.aggregateLimitOverride?.toString() || originals.aggregateLimit?.toString() || '',
    aggregateLimitApplicable: overrides.noLimit === undefined ? true : !overrides.noLimit,
    occurrenceLimitAmount: overrides.occurrenceLimitOverride?.toString() || originals.occurrenceLimit?.toString() || '',
    occurrenceLimitApplicable: overrides.noOccurrenceLimit === undefined ? true : !overrides.noOccurrenceLimit,
    quotaShareTotal: overrides.quotaShareTotalOverride?.toString() || originals.quotaShareTotal?.toString() || '',
    retentionAmount: overrides.retentionOverride?.toString() || originals.retention?.toString() || '',
    retentionApplicable: peFlag === 'Excess' && !hasRetention ? false : overrides.noRetention === undefined ? true : !overrides.noRetention,
    leadSyndicateId: overrides.syndicateId?.toString() || originals.syndicateId?.toString() || '',
    sideAValue: overrides.sideAOverride ?? originals.sideA ?? '',
    broker: '',
    runoff: overrides.runoffOverride ?? originals.runoff === 'Runoff',
    carrierId: carrierNameToId.get((overrides.carrierOverride || originals.carrier || '').trim()) ?? '',
    premiumTotal: overrides.policyPremiumOverride?.toString() || originals.policyPremium?.toString() || '',
    description: overrides.descriptionOverride ?? originals.description ?? '',
    policyNumber: overrides.policyNumberOverride ?? originals.policyNumber ?? '',
    wholesaler: overrides.wholesalerOverride ?? originals.wholesaler ?? '',
    agencyName: overrides.agencyNameOverride ?? originals.agencyName ?? '',
    coverageType: overrides.coverageTypeOverride ?? originals.coverageType ?? '',
    effectiveDate: toDateInput(overrides.effectiveDateOverride ?? originals.effectiveDate),
    expirationDate: toDateInput(overrides.expirationDateOverride ?? originals.expirationDate),
    isQuotaShare: overrides.quotaShareOverride ?? originals.quotaShare ?? false,
  } as FormSchemeType;
}

// An emptied amount saves as not applicable. A NULL override on its own falls back to the original
// value, so the cleared field would revert.
function toAmountOverride(amount: string, applicable: boolean): [number | undefined, boolean] {
  const hasAmount = applicable && amount.trim() !== '';
  return [hasAmount ? Number(amount) : undefined, !hasAmount];
}

function toOptionalNumber(value: string): number | undefined {
  return value !== '' ? Number(value) : undefined;
}

function toPolicyLineOverride(form: FormSchemeType): PolicyLineOverride {
  const [attachmentOverride, noAttachment] = toAmountOverride(form.attachmentPointAmount, form.attachmentApplicable);
  const [aggregateLimitOverride, noLimit] = toAmountOverride(form.aggregateLimitAmount, form.aggregateLimitApplicable);
  const [occurrenceLimitOverride, noOccurrenceLimit] = toAmountOverride(form.occurrenceLimitAmount, form.occurrenceLimitApplicable);
  const [retentionOverride, noRetention] = toAmountOverride(form.retentionAmount, form.retentionApplicable);
  return {
    attachmentOverride,
    noAttachment,
    aggregateLimitOverride,
    noLimit,
    occurrenceLimitOverride,
    noOccurrenceLimit,
    quotaShareTotalOverride: toOptionalNumber(form.quotaShareTotal),
    retentionOverride,
    noRetention,
    policyPremiumOverride: toOptionalNumber(form.premiumTotal),
  };
}

function toPolicyOverride(form: FormSchemeType, caps: PolicyCaps): PolicyOverride {
  return {
    ...toPolicyLineOverride(form),
    peFlag: form.peFlag ?? '',
    syndicateId: form.leadSyndicateId ? Number(form.leadSyndicateId) : undefined,
    sideAOverride: form.sideAValue,
    runoffOverride: form.runoff,
    carrierOverride: form.carrierId ? caps.carrierIdToName.get(form.carrierId) : undefined,
    // A cleared field is sent as '' rather than undefined, which would fall back to the original.
    descriptionOverride: form.description.trim(),
    policyNumberOverride: form.policyNumber.trim(),
    wholesalerOverride: form.wholesaler.trim(),
    agencyNameOverride: form.agencyName.trim(),
    coverageTypeOverride: form.coverageType,
    effectiveDateOverride: toIsoDatetime(form.effectiveDate),
    expirationDateOverride: toIsoDatetime(form.expirationDate),
    quotaShareOverride: form.isQuotaShare,
  } as PolicyOverride;
}

function isEqual(a: FormSchemeType, b: FormSchemeType): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

interface FieldChange {
  field: string;
  newValueRaw: string | number | boolean | undefined;
  oldValueRaw: string | number | boolean | undefined;
}

const amountFields = [
  { amount: 'attachmentPointAmount', applicable: 'attachmentApplicable', override: 'attachmentOverride', flag: 'noAttachment' },
  { amount: 'aggregateLimitAmount', applicable: 'aggregateLimitApplicable', override: 'aggregateLimitOverride', flag: 'noLimit' },
  { amount: 'occurrenceLimitAmount', applicable: 'occurrenceLimitApplicable', override: 'occurrenceLimitOverride', flag: 'noOccurrenceLimit' },
  { amount: 'retentionAmount', applicable: 'retentionApplicable', override: 'retentionOverride', flag: 'noRetention' },
] as const;

function diffPolicyFields(
  form: FormSchemeType,
  original: FormSchemeType,
  loaded: Partial<PolicyOverride>,
  mode: 'policy' | 'line',
): FieldChange[] {
  const changes: FieldChange[] = [];

  const pushIfChanged = (field: string, newVal: unknown, oldVal: unknown) => {
    if (newVal !== oldVal) {
      changes.push({
        field,
        newValueRaw: newVal as FieldChange['newValueRaw'],
        oldValueRaw: oldVal as FieldChange['oldValueRaw'],
      });
    }
  };

  if (mode === 'policy') {
    pushIfChanged('peFlag', form.peFlag, original.peFlag);
  }

  for (const field of amountFields) {
    const amountNew = form[field.applicable] ? form[field.amount] : '';
    const amountOld = original[field.applicable] ? original[field.amount] : '';
    pushIfChanged(field.override, amountNew, amountOld);

    // Compared with the loaded flag rather than the toggle, since an emptied box saves as not
    // applicable even before its blur has switched the toggle off.
    if (form[field.amount] !== original[field.amount] || form[field.applicable] !== original[field.applicable]) {
      const [, notApplicable] = toAmountOverride(form[field.amount], form[field.applicable]);
      pushIfChanged(field.flag, !notApplicable, !loaded[field.flag]);
    }
  }

  pushIfChanged('quotaShareTotalOverride', form.quotaShareTotal, original.quotaShareTotal);

  if (mode === 'policy') {
    pushIfChanged('syndicateId', form.leadSyndicateId, original.leadSyndicateId);
    pushIfChanged('runoffOverride', form.runoff, original.runoff);
    pushIfChanged('sideAOverride', form.sideAValue, original.sideAValue);
    // carrierId holds the rInsurerGroup ID; the review screen maps it to the name via valueLabels.
    pushIfChanged('carrierOverride', form.carrierId, original.carrierId);
    pushIfChanged('policyPremiumOverride', form.premiumTotal, original.premiumTotal);
    for (const field of externalFields) {
      pushIfChanged(field.override, form[field.form], original[field.form]);
    }
    pushIfChanged('quotaShareOverride', form.isQuotaShare, original.isQuotaShare);
  }

  if (mode === 'line') {
    pushIfChanged('policyPremiumOverride', form.premiumTotal, original.premiumTotal);
  }

  return changes;
}

export type ValueLabelLookup = Record<string, Map<string, string>>;

function formatDiffValue(field: string, raw: unknown, lookups?: ValueLabelLookup): string {
  if (raw === undefined || raw === null || raw === '') return '';
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  const strVal = String(raw);
  const lookup = lookups?.[field];
  if (lookup?.has(strVal)) return lookup.get(strVal)!;
  const label = policyFieldLabels[field];
  if (label?.formatter) {
    return label.formatter(strVal);
  }
  return strVal;
}

const savedRowKey = (kind: SubitemKind, itemId: number): string => `${kind}-saved-${itemId}`;

function stagedFor(state: RecordState, kind: SubitemKind, rowKey: string): StagedSubitem | undefined {
  return state.stagedSubitems.find((entry) => entry.kind === kind && entry.rowKey === rowKey);
}

// buildSubitemRows merges the saved sublimits/subretentions for a line with whatever the user has
// staged, so the panel renders one list: unchanged rows, edited rows, rows marked for removal, and
// rows added this session (which have no saved counterpart).
function buildSubitemRows(state: RecordState, kind: SubitemKind): SubitemRow[] {
  const saved = kind === 'sublimit' ? state.sublimits : state.subretentions;

  const rows: SubitemRow[] = saved.map((item) => {
    const rowKey = savedRowKey(kind, item.id);
    const staged = stagedFor(state, kind, rowKey);
    const basis = (item as PolicySubretention).basis ?? '';
    return {
      rowKey,
      kind,
      itemId: item.id,
      typeId: kind === 'sublimit' ? (item as PolicySublimit).sublimitTypeId : (item as PolicySubretention).subretentionTypeId,
      name: item.name,
      value: staged?.op === 'update' ? (staged.value ?? '') : item.value.toString(),
      basis: staged?.op === 'update' ? (staged.basis ?? '') : basis,
      savedValue: item.value,
      savedBasis: basis,
      originalValue: item.originalValue ?? undefined,
      status: (staged?.op === 'delete' ? 'deleted' : staged?.op === 'update' ? 'updated' : 'unchanged') as SubitemStatus,
      lockedByCoverageChange: staged?.cause === 'coverageType',
    };
  });

  for (const staged of state.stagedSubitems) {
    if (staged.kind !== kind || staged.op !== 'add') continue;
    rows.push({
      rowKey: staged.rowKey,
      kind,
      typeId: staged.typeId!,
      name: staged.typeName ?? '',
      value: staged.value ?? '',
      basis: staged.basis ?? '',
      status: 'added',
    });
  }

  return rows;
}

function subitemDiffs(state: RecordState): DiffEntry[] {
  return state.stagedSubitems.map((staged) => {
    const label = subitemLabel(staged.kind, staged.typeName ?? '');
    const savedList = staged.kind === 'sublimit' ? state.sublimits : state.subretentions;
    const savedItem = savedList.find((item) => item.id === staged.itemId);
    const savedValue = savedItem ? compactMoney(savedItem.value) : '';

    if (staged.op === 'add') {
      return { field: staged.rowKey, label, oldValue: '', newValue: formatSubitemValue(staged) };
    }
    if (staged.op === 'delete') {
      return { field: staged.rowKey, label, oldValue: savedValue, newValue: '' };
    }
    return { field: staged.rowKey, label, oldValue: savedValue, newValue: formatSubitemValue(staged) };
  });
}

function formatSubitemValue(staged: StagedSubitem): string {
  const amount = staged.value === '' || staged.value === undefined ? '' : compactMoney(Number(staged.value));
  if (staged.kind === 'subretention' && staged.basis) {
    return amount ? `${amount} (${staged.basis})` : staged.basis;
  }
  return amount;
}

function toSublimitOp(staged: StagedSubitem, uniqLine?: string): SublimitOpPayload {
  if (staged.op === 'delete') {
    return { operation: 'delete', policySublimitId: staged.itemId };
  }
  const limitValue = Number(staged.value || 0);
  if (staged.op === 'update') {
    return { operation: 'update', policySublimitId: staged.itemId, limitValue };
  }
  return { operation: 'add', policyLineId: uniqLine, sublimitTypeId: staged.typeId, limitValue };
}

function toSubretentionOp(staged: StagedSubitem, uniqLine?: string): SubretentionOpPayload {
  if (staged.op === 'delete') {
    return { operation: 'delete', policySubretentionId: staged.itemId };
  }
  const retentionValue = Number(staged.value || 0);
  const retentionBasis = staged.basis || undefined;
  if (staged.op === 'update') {
    return { operation: 'update', policySubretentionId: staged.itemId, retentionValue, retentionBasis };
  }
  return { operation: 'add', policyLineId: uniqLine, subretentionTypeId: staged.typeId, retentionValue, retentionBasis };
}

export function usePolicyEditPending() {
  const states = reactive(new Map<RecordKey, RecordState>());
  // The last policy-wide fetch, kept so a record that learns its line after the fetch (a monoline
  // policy record, whose line arrives with its override) can still be given its rows.
  let fetchedSubitems: { sublimits: PolicySublimit[]; subretentions: PolicySubretention[] } | null = null;

  const caps: PolicyCaps = {
    carrierIdToName: new Map(),
    carrierNameToId: new Map(),
    allowCarrierUpdate: false,
    allowPremiumUpdate: false,
    allowExternalFieldUpdate: false,
  };

  // Must be called (with reference data + policy source loaded) before loadRecord so toForm can
  // resolve the carrier name to its Select id, and before buildBatchPayload for the reverse.
  function configureCaps(opts: {
    carrierOptions: { label: string; value: string }[];
    allowCarrierUpdate: boolean;
    allowPremiumUpdate: boolean;
    allowExternalFieldUpdate?: boolean;
  }): void {
    caps.carrierIdToName = new Map(opts.carrierOptions.map((o) => [o.value, o.label]));
    caps.carrierNameToId = new Map(opts.carrierOptions.map((o) => [o.label.trim(), o.value]));
    caps.allowCarrierUpdate = opts.allowCarrierUpdate;
    caps.allowPremiumUpdate = opts.allowPremiumUpdate;
    caps.allowExternalFieldUpdate = !!opts.allowExternalFieldUpdate;
  }

  function init(refs: RecordRef[]) {
    states.clear();
    fetchedSubitems = null;
    for (const ref of refs) {
      states.set(ref.key, {
        ref: { ...ref },
        loaded: false,
        form: formSchema.parse({}),
        originalForm: formSchema.parse({}),
        validFields: allFieldsValid(),
        validationMessages: {},
        loadedOverride: {},
        sublimits: [],
        subretentions: [],
        sublimitTypes: [],
        subretentionTypes: [],
        subitemTypesLoaded: false,
        stagedSubitems: [],
      });
    }
  }

  async function loadRecord(key: RecordKey): Promise<void> {
    const state = states.get(key);
    if (!state || state.loaded) return;

    if (state.ref.type === 'policy') {
      const res = await policyOverrideService.GetCurrentOverride(state.ref.uniqPolicy);
      const form = toForm(res.originalValues, res.policyOverride, caps.carrierNameToId);
      state.form = form;
      state.originalForm = structuredClone(form);
      state.loadedOverride = res.policyOverride;
      if (res.policyLineId) {
        state.ref.uniqLine = String(res.policyLineId);
        assignSubitems(state);
      }
    } else {
      const res = await policyOverrideService.GetCurrentLineOverride(state.ref.uniqPolicy, state.ref.uniqLine!);
      const form = toForm(res.originalValues, res.policyOverride, caps.carrierNameToId);
      state.form = form;
      state.originalForm = structuredClone(form);
      state.loadedOverride = res.policyOverride;
    }
    state.loaded = true;
  }

  let newSubitemCounter = 0;

  function assignSubitems(state: RecordState): void {
    if (!fetchedSubitems || !state.ref.uniqLine) return;
    const lineID = Number(state.ref.uniqLine);
    state.sublimits = fetchedSubitems.sublimits.filter((item) => item.policyLineId === lineID);
    state.subretentions = fetchedSubitems.subretentions.filter((item) => item.policyLineId === lineID);
  }

  // Sublimits and subretentions are fetched per policy, then fanned out to the records that own a
  // line — one round trip each rather than one per line.
  async function loadSubitems(policyID: string): Promise<void> {
    const [sublimits, subretentions] = await Promise.all([policyService.sublimits(policyID), policyService.subretentions(policyID)]);
    // An empty list can arrive as null; without this one empty kind would abort the fan-out and
    // blank the other kind on every line too.
    fetchedSubitems = { sublimits: sublimits ?? [], subretentions: subretentions ?? [] };
    states.forEach(assignSubitems);
  }

  // The selectable types are scoped server-side to the line's coverage type and already exclude
  // types the line carries; staged adds are excluded here so a type cannot be picked twice.
  async function loadSubitemTypes(key: RecordKey): Promise<void> {
    const state = states.get(key);
    if (!state || state.subitemTypesLoaded || !state.ref.uniqLine) return;

    const [sublimitTypes, subretentionTypes] = await Promise.all([
      policyService.sublimitTypes(state.ref.uniqPolicy, state.ref.uniqLine),
      policyService.subretentionTypes(state.ref.uniqPolicy, state.ref.uniqLine),
    ]);
    state.sublimitTypes = sublimitTypes;
    state.subretentionTypes = subretentionTypes;
    state.subitemTypesLoaded = true;
  }

  function subitemLineId(key: RecordKey): string | undefined {
    return states.get(key)?.ref.uniqLine;
  }

  function availableSubitemTypes(key: RecordKey, kind: SubitemKind): SubitemTypeRef[] {
    const state = states.get(key);
    if (!state) return [];
    const types = kind === 'sublimit' ? state.sublimitTypes : state.subretentionTypes;
    const stagedTypeIds = new Set(state.stagedSubitems.filter((entry) => entry.kind === kind && entry.op === 'add').map((entry) => entry.typeId));
    return types.filter((type) => !stagedTypeIds.has(type.id));
  }

  function subitemRows(key: RecordKey, kind: SubitemKind): SubitemRow[] {
    const state = states.get(key);
    return state ? buildSubitemRows(state, kind) : [];
  }

  function stageSubitemAdd(key: RecordKey, kind: SubitemKind, type: SubitemTypeRef): string {
    const state = states.get(key);
    if (!state) return '';
    const rowKey = `${kind}-new-${++newSubitemCounter}`;
    state.stagedSubitems.push({ kind, op: 'add', rowKey, typeId: type.id, typeName: type.name, value: '', basis: '' });
    return rowKey;
  }

  // matchesSavedSubitem compares an edit against the stored row rather than against the staged
  // op, so a value typed back to its original reads as unchanged however many edits preceded it.
  function matchesSavedSubitem(state: RecordState, kind: SubitemKind, itemId: number | undefined, value: string, basis: string): boolean {
    if (itemId === undefined) return false;
    const saved = (kind === 'sublimit' ? state.sublimits : state.subretentions).find((item) => item.id === itemId);
    if (!saved) return false;
    return value === saved.value.toString() && basis === ((saved as PolicySubretention).basis ?? '');
  }

  function setSubitemValue(key: RecordKey, kind: SubitemKind, rowKey: string, value: string, basis?: string): void {
    const state = states.get(key);
    if (!state) return;

    const staged = stagedFor(state, kind, rowKey);
    if (staged?.op === 'delete') return;

    if (staged) {
      staged.value = value;
      if (basis !== undefined) staged.basis = basis;
      // An update edited back to the saved value is no longer a change, so drop the op — otherwise
      // the row keeps its Edited badge and the record stays dirty. An add has nothing to revert to.
      if (staged.op === 'update' && matchesSavedSubitem(state, kind, staged.itemId, value, staged.basis ?? '')) {
        state.stagedSubitems = state.stagedSubitems.filter((entry) => !(entry.kind === kind && entry.rowKey === rowKey));
      }
      return;
    }

    const row = buildSubitemRows(state, kind).find((candidate) => candidate.rowKey === rowKey);
    if (!row?.itemId) return;

    const nextBasis = basis !== undefined ? basis : (row.savedBasis ?? '');
    if (matchesSavedSubitem(state, kind, row.itemId, value, nextBasis)) return;

    state.stagedSubitems.push({ kind, op: 'update', rowKey, itemId: row.itemId, typeId: row.typeId, typeName: row.name, value, basis: nextBasis });
  }

  function stageSubitemDelete(key: RecordKey, kind: SubitemKind, rowKey: string): void {
    const state = states.get(key);
    if (!state) return;

    const staged = stagedFor(state, kind, rowKey);
    if (staged?.cause === 'coverageType') return;
    // Removing a row added this session just drops the add; nothing was ever saved.
    if (staged?.op === 'add') {
      state.stagedSubitems = state.stagedSubitems.filter((entry) => entry !== staged);
      return;
    }

    const row = buildSubitemRows(state, kind).find((candidate) => candidate.rowKey === rowKey);
    if (!row?.itemId) return;

    // A pending edit on the row is superseded by the removal.
    state.stagedSubitems = state.stagedSubitems.filter((entry) => !(entry.kind === kind && entry.rowKey === rowKey));
    state.stagedSubitems.push({ kind, op: 'delete', rowKey, itemId: row.itemId, typeId: row.typeId, typeName: row.name });
  }

  function unstageSubitem(key: RecordKey, kind: SubitemKind, rowKey: string): void {
    const state = states.get(key);
    if (!state || stagedFor(state, kind, rowKey)?.cause === 'coverageType') return;
    state.stagedSubitems = state.stagedSubitems.filter((entry) => !(entry.kind === kind && entry.rowKey === rowKey));
  }

  function stagedSubitemCount(key: RecordKey, kind: SubitemKind): number {
    return states.get(key)?.stagedSubitems.filter((entry) => entry.kind === kind).length ?? 0;
  }

  // Sublimit and subretention types belong to a coverage type, so a line that changes coverage
  // type loses all of them. These are the rows a change would take off the line as the user sees it.
  function coverageChangeRemovals(key: RecordKey): { sublimits: number; subretentions: number } {
    const state = states.get(key);
    if (!state || state.coverageChangeSnapshot) return { sublimits: 0, subretentions: 0 };
    const remaining = (kind: SubitemKind) => buildSubitemRows(state, kind).filter((row) => row.status !== 'deleted').length;
    return { sublimits: remaining('sublimit'), subretentions: remaining('subretention') };
  }

  // setCoverageType stages a coverage type change together with the removal of every saved
  // sublimit and subretention on the line, dropping anything staged for them. Choosing the original
  // coverage type again puts the staged subitems back as they were.
  function setCoverageType(key: RecordKey, coverageType: string): void {
    const state = states.get(key);
    if (!state || !coverageType) return;

    if (coverageType === state.originalForm.coverageType) {
      restoreCoverageChangeSnapshot(state);
    } else if (!state.coverageChangeSnapshot) {
      state.coverageChangeSnapshot = state.stagedSubitems.map((entry) => ({ ...entry }));
      state.stagedSubitems = [
        ...state.sublimits.map((item) => coverageChangeRemoval('sublimit', item.id, item.sublimitTypeId, item.name)),
        ...state.subretentions.map((item) => coverageChangeRemoval('subretention', item.id, item.subretentionTypeId, item.name)),
      ];
    }
    state.form.coverageType = coverageType;
  }

  function undoCoverageTypeChange(key: RecordKey): void {
    const state = states.get(key);
    if (!state) return;
    restoreCoverageChangeSnapshot(state);
    state.form.coverageType = state.originalForm.coverageType;
  }

  function restoreCoverageChangeSnapshot(state: RecordState): void {
    if (!state.coverageChangeSnapshot) return;
    state.stagedSubitems = state.coverageChangeSnapshot;
    state.coverageChangeSnapshot = undefined;
  }

  function coverageChangeRemoval(kind: SubitemKind, itemId: number, typeId: number, typeName: string): StagedSubitem {
    return { kind, op: 'delete', rowKey: savedRowKey(kind, itemId), itemId, typeId, typeName, cause: 'coverageType' };
  }

  // The selectable types are still the old coverage type's until the change is saved.
  function coverageTypeChangeStaged(key: RecordKey): boolean {
    return !!states.get(key)?.coverageChangeSnapshot;
  }

  function get(key: RecordKey): RecordState | undefined {
    return states.get(key);
  }

  function isDirty(key: RecordKey): boolean {
    const s = states.get(key);
    if (!s) return false;
    if (s.stagedSubitems.length > 0) return true;
    if (!s.loaded) return false;
    return !isEqual(s.form, s.originalForm);
  }

  const dirtyKeys = computed<RecordKey[]>(() => {
    const out: RecordKey[] = [];
    states.forEach((s, k) => {
      if (s.stagedSubitems.length > 0 || (s.loaded && !isEqual(s.form, s.originalForm))) {
        out.push(k);
      }
    });
    return out;
  });

  const totalFieldChanges = computed(() => {
    let count = 0;
    states.forEach((s) => {
      count += s.stagedSubitems.length;
      if (!s.loaded) return;
      count += diffPolicyFields(s.form, s.originalForm, s.loadedOverride, s.ref.type).length;
    });
    return count;
  });

  function validate(key: RecordKey): boolean {
    const s = states.get(key);
    if (!s) return false;
    const parsed = formSchema.safeParse(s.form);
    s.validFields = allFieldsValid();
    s.validationMessages = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        s.validFields[issue.path[0]?.toString() as keyof ValidFormFields] = false;
      }
    }
    const missing = missingRequiredFields(s);
    for (const field of missing) s.validFields[field] = false;
    if (s.ref.type === 'policy' && caps.allowExternalFieldUpdate) {
      validateExternalFields(s);
    }
    return parsed.success && missing.length === 0 && Object.keys(s.validationMessages).length === 0;
  }

  // Quota share, premium and syndicate have no not-applicable flag, so emptying one would save NULL
  // and fall back to the original value. Like the policy number, a value that loaded can't be cleared.
  function missingRequiredFields(s: RecordState): (keyof FormSchemeType)[] {
    const missing: (keyof FormSchemeType)[] = requiredOnceSetFields.filter((field) => !s.form[field].trim() && !!s.originalForm[field].trim());
    if (s.ref.type === 'policy' && caps.allowCarrierUpdate && !s.form.carrierId) missing.push('carrierId');
    return missing;
  }

  // The upload template's rules for the fields only external policies can edit.
  function validateExternalFields(s: RecordState): void {
    const invalid = (field: keyof FormSchemeType, message: string) => {
      s.validFields[field] = false;
      s.validationMessages[field] = message;
    };
    const { form, originalForm } = s;

    if (!form.coverageType && !!originalForm.coverageType) invalid('coverageType', 'coverage type is required');
    if (!form.effectiveDate && !!originalForm.effectiveDate) invalid('effectiveDate', 'effective date is required');
    if (!form.expirationDate && !!originalForm.expirationDate) invalid('expirationDate', 'expiration date is required');

    // Like the API, the term is only checked when it changes, so a policy uploaded with bad dates
    // can still take unrelated edits.
    const termChanged = form.effectiveDate !== originalForm.effectiveDate || form.expirationDate !== originalForm.expirationDate;
    if (termChanged && form.effectiveDate && form.expirationDate && form.expirationDate <= form.effectiveDate) {
      invalid('expirationDate', 'must be after the effective date');
    }

    if (form.coverageType !== originalForm.coverageType && form.coverageType === WORK_COMP_COVERAGE_TYPE && hasApplicableLimit(form)) {
      invalid('coverageType', 'Work Comp cannot have an aggregate or occurrence limit');
    }
  }

  // The save enforces the same rule; checking before review puts the clash next to the field.
  async function checkPolicyNumber(key: RecordKey): Promise<boolean> {
    const s = states.get(key);
    if (!s || !s.loaded || s.ref.type !== 'policy' || !caps.allowExternalFieldUpdate) return true;

    const { policyNumber, effectiveDate, expirationDate } = s.form;
    const unchanged =
      policyNumber === s.originalForm.policyNumber && effectiveDate === s.originalForm.effectiveDate && expirationDate === s.originalForm.expirationDate;
    if (unchanged || !policyNumber.trim() || !effectiveDate || !expirationDate) return true;

    const result = await policyOverrideService.policyNumberConflict(s.ref.uniqPolicy, {
      policyNumber: policyNumber.trim(),
      effectiveDate,
      expirationDate,
    });
    if (!result.conflict) return true;

    s.validFields.policyNumber = false;
    s.validationMessages.policyNumber = result.message ?? 'policy number is already used on this account';
    return false;
  }

  function validateAll(): boolean {
    let ok = true;
    states.forEach((_, k) => {
      if (!validate(k)) ok = false;
    });
    return ok;
  }

  function buildDiffs(key: RecordKey, valueLabels?: ValueLabelLookup): DiffEntry[] {
    const s = states.get(key);
    if (!s) return [];
    const fieldDiffs = s.loaded
      ? diffPolicyFields(s.form, s.originalForm, s.loadedOverride, s.ref.type).map((c) => ({
          field: c.field,
          label: policyFieldLabels[c.field]?.label ?? c.field,
          oldValue: formatDiffValue(c.field, c.oldValueRaw, valueLabels),
          newValue: formatDiffValue(c.field, c.newValueRaw, valueLabels),
        }))
      : [];
    return [...fieldDiffs, ...subitemDiffs(s)];
  }

  function buildBatchPayload(): PolicyOverrideBatchPayload {
    const payload: PolicyOverrideBatchPayload = {};
    const sublimitOps: SublimitOpPayload[] = [];
    const subretentionOps: SubretentionOpPayload[] = [];

    states.forEach((s) => {
      for (const staged of s.stagedSubitems) {
        if (staged.kind === 'sublimit') {
          sublimitOps.push(toSublimitOp(staged, s.ref.uniqLine));
        } else {
          subretentionOps.push(toSubretentionOp(staged, s.ref.uniqLine));
        }
      }
    });

    if (sublimitOps.length) payload.sublimitOps = sublimitOps;
    if (subretentionOps.length) payload.subretentionOps = subretentionOps;

    states.forEach((s) => {
      if (!s.loaded || isEqual(s.form, s.originalForm)) return;

      if (s.ref.type === 'policy') {
        payload.policyOverride = policyOverrideFor(s);
      } else if (s.ref.uniqLine) {
        if (!payload.lineOverrides) payload.lineOverrides = {};
        payload.lineOverrides[s.ref.uniqLine] = lineOverrideFor(s);
      }
    });

    return payload;
  }

  function policyOverrideFor(s: RecordState): PolicyOverride {
    const override = carryUntouched(toPolicyOverride(s.form, caps), s.loadedOverride, s.form, s.originalForm, policyFieldGroups);
    // Carrier/premium are only sent when the source permits it — otherwise the API's
    // validateCanUpdateCarrier/Premium gates reject the whole save.
    if (!caps.allowCarrierUpdate) override.carrierOverride = undefined;
    if (!caps.allowPremiumUpdate) override.policyPremiumOverride = undefined;
    // Without the cap an external-field edit is ignored, but the saved overrides are still carried:
    // dropping them would null them on any other edit.
    if (!caps.allowExternalFieldUpdate) {
      for (const { override: key } of externalFields) override[key] = s.loadedOverride[key] ?? undefined;
      override.quotaShareOverride = s.loadedOverride.quotaShareOverride ?? undefined;
    }
    return override;
  }

  function lineOverrideFor(s: RecordState): PolicyLineOverride {
    return carryUntouched(toPolicyLineOverride(s.form), s.loadedOverride, s.form, s.originalForm, lineFieldGroups);
  }

  function resetOriginalsFromCurrent(): void {
    states.forEach((s) => {
      if (s.loaded) {
        if (!isEqual(s.form, s.originalForm)) {
          s.loadedOverride = s.ref.type === 'policy' ? policyOverrideFor(s) : lineOverrideFor(s);
        }
        // form is reactive; structuredClone can't clone proxies, so round-trip via JSON
        s.originalForm = JSON.parse(JSON.stringify(s.form)) as FormSchemeType;
      }
      // Staged subitem ops have been applied by the save, so clearing them stops a second save
      // replaying the same adds and deletes. The caller must follow with loadSubitems — the
      // stored rows have changed and sublimits/subretentions here are still the pre-save lists.
      s.stagedSubitems = [];
      s.coverageChangeSnapshot = undefined;
      s.subitemTypesLoaded = false;
    });
  }

  return {
    states,
    init,
    configureCaps,
    loadRecord,
    loadSubitems,
    loadSubitemTypes,
    subitemLineId,
    availableSubitemTypes,
    subitemRows,
    stageSubitemAdd,
    setSubitemValue,
    stageSubitemDelete,
    unstageSubitem,
    stagedSubitemCount,
    coverageChangeRemovals,
    setCoverageType,
    undoCoverageTypeChange,
    coverageTypeChangeStaged,
    checkPolicyNumber,
    get,
    isDirty,
    dirtyKeys,
    totalFieldChanges,
    validate,
    validateAll,
    buildDiffs,
    buildBatchPayload,
    resetOriginalsFromCurrent,
  };
}
